import {
  api as coreApi,
  canonicalGroupThreadKey,
  crypto as coreCrypto,
  encodeBase64,
  decodeBase64,
  generateUUID,
  CommunityUpdateReason,
  MessageType,
  WebSocketEventType,
  type Community,
  type CommunityGroup,
  type Member,
  type Thread,
} from "@/core_bridge/module_api";
import { parseJwt, useStore } from "@/state/legacy_module_api";
import { send as sendWebSocket } from "@/transport/webSocketService";
import { encryptDMMessageWithPublicKey } from "@/services/encryption";
import { resolveContactPublicKey, serializeContactPublicKeys } from "@/data/adapters/contact_keys";
import * as db from "@/services/effects/db/storage";
import { appConfig } from "@/app/module_api";

export type CommunityRole = "owner" | "admin" | "moderator" | "member";

export type CommunityInviteInput = {
  community: Community;
  userId: string;
  role?: CommunityRole;
};

export type CommunityRoleUpdateInput = {
  community: Community;
  userId: string;
  role: CommunityRole;
};

export type CommunityLeaveInput = {
  community: Community;
};

export type CommunityUpdatePayloadInput = {
  communityId: string;
  communityKey: string;
  encryptedData?: string | null;
  iv?: string | null;
  version?: number | null;
  updatedAt?: number | null;
  reason?: string | null;
  memberId?: string | null;
  memberRole?: CommunityRole | null;
};

function nowMs(): number {
  return Date.now();
}

function resolveImageExtension(mimeType?: string | null): string {
  const normalized = (mimeType || "").toLowerCase();
  if (normalized.includes("png")) return "png";
  if (normalized.includes("webp")) return "webp";
  if (normalized.includes("gif")) return "gif";
  return "jpg";
}

async function uploadCommunityImageBlob(blob: Blob, mimeType?: string | null): Promise<string> {
  const ext = resolveImageExtension(mimeType || blob.type);
  const presigned = await coreApi.getPresignedUrl(`community-${generateUUID()}.${ext}`);
  const uploadUrl = presigned?.url;
  if (!uploadUrl) {
    throw new Error("Failed to get presigned URL for community image upload");
  }

  const uploadResponse = await fetch(uploadUrl, {
    method: "PUT",
    body: blob,
    headers: {
      "Content-Type": mimeType || blob.type || "application/octet-stream",
      "x-amz-acl": "public-read",
    },
  });
  if (!uploadResponse.ok) {
    throw new Error(`Community image upload failed: ${uploadResponse.status}`);
  }

  return String(uploadUrl).split("?")[0];
}

function resolveCurrentUserId(state: any): string | undefined {
  const tokenData = state.apiToken ? parseJwt(state.apiToken) : {};
  return tokenData.user_id || undefined;
}

function resolveMySecretKey(state: any): string | undefined {
  return (
    state.keyPair?.base64?.secretKey ||
    (state.keyPair as any)?.base64?.secret_key ||
    state.keyPair?.secretKey ||
    state.keyPair?.secret_key
  );
}

function resolveMyPublicKey(state: any): string | undefined {
  return (
    state.keyPair?.base64?.publicKey ||
    (state.keyPair as any)?.base64?.public_key ||
    state.keyPair?.publicKey ||
    state.keyPair?.public_key
  );
}

function encodeJson(data: unknown): string {
  return JSON.stringify(data);
}

function communityBlobPlainData(community: Community): Community {
  return {
    ...community,
    description: community.description || null,
    image: community.image || null,
    members: community.members || [],
    groups: community.groups || [],
    createdAt: Number(community.createdAt || nowMs()),
    updatedAt: Number(community.updatedAt || nowMs()),
    isActive: community.isActive ?? true,
    version: community.version || 1,
  } as Community;
}

function encryptWithKey(data: unknown, keyBase64: string): { encryptedData: string; iv: string } {
  const key = decodeBase64(keyBase64);
  const nonce = coreCrypto.secretboxNonce();
  const plaintext = new TextEncoder().encode(JSON.stringify(data));
  const ciphertext = coreCrypto.secretboxSeal(plaintext, nonce, key);
  return {
    encryptedData: encodeBase64(ciphertext),
    iv: encodeBase64(nonce),
  };
}

function decryptWithKey(encryptedData: string, iv: string, keyBase64: string): any | null {
  try {
    const key = decodeBase64(keyBase64);
    const ciphertext = decodeBase64(encryptedData);
    const nonce = decodeBase64(iv);
    const plaintext = coreCrypto.secretboxOpen(ciphertext, nonce, key);
    if (!plaintext) return null;
    const decoded = new TextDecoder().decode(plaintext);
    return JSON.parse(decoded);
  } catch (err) {
    console.warn("[community] decryptWithKey failed", err);
    return null;
  }
}

function getCommunityMemberRole(community: Community, userId?: string): CommunityRole | undefined {
  if (!userId) return undefined;
  const member = community.members?.find((m) => m.userId === userId);
  return normalizeMemberRole(member?.role);
}

function canManageMembers(community: Community, userId?: string): boolean {
  const role = getCommunityMemberRole(community, userId);
  return role === "owner" || role === "admin";
}

function isOwner(community: Community, userId?: string): boolean {
  return getCommunityMemberRole(community, userId) === "owner";
}

function normalizeCommunityGroups(community: Community): CommunityGroup[] {
  return Array.isArray(community.groups) ? community.groups : [];
}

function communityGroupThreadKey(groupId: string): string {
  return canonicalGroupThreadKey(groupId) || (groupId.startsWith("group:") ? groupId : `group:${groupId}`);
}

function normalizeMemberRole(role: unknown): CommunityRole | undefined {
  if (role === null || role === undefined) return undefined;
  const normalized = String(role).trim().toLowerCase();
  if (!normalized) return undefined;
  if (normalized === "owner" || normalized === "admin" || normalized === "moderator" || normalized === "member") {
    return normalized as CommunityRole;
  }
  return undefined;
}

function normalizeCommunityMembers(community: Community): Community {
  if (!Array.isArray(community.members) || community.members.length === 0) {
    return community;
  }
  const normalizedMembers = community.members.map((member) => {
    const normalizedRole = normalizeMemberRole((member as any).role);
    if (!normalizedRole || normalizedRole === (member as any).role) return member;
    return { ...member, role: normalizedRole } as Member;
  });
  return { ...community, members: normalizedMembers };
}

function resolveContactPublicKeyByUserId(state: any, userId: string): string | undefined {
  const contact = state.contactsById?.[userId];
  return resolveContactPublicKey(contact);
}

async function fetchAndStoreContactByUserId(state: any, userId: string): Promise<string | undefined> {
  if (!userId) return undefined;
  try {
    const response = await coreApi.searchUserId(userId);
    const user = response?.user;
    if (!user) return undefined;

    const rawKeys = user.publicKeys || user.public_keys || [];
    const apiDeviceId =
      user.deviceId ||
      user.device_id ||
      (Array.isArray(rawKeys) ? rawKeys[0]?.deviceId || rawKeys[0]?.device_id : undefined) ||
      "default";
    const normalizedKeys = (Array.isArray(rawKeys) ? rawKeys : [])
      .map((entry: any) => {
        const publicKey = entry?.publicKey ?? entry?.public_key;
        if (!publicKey) return null;
        const deviceId = entry?.deviceId ?? entry?.device_id ?? apiDeviceId ?? "default";
        return {
          publicKey: String(publicKey).replace(/\s+/g, ""),
          deviceId,
          mlKemPublicKey: entry?.mlKemPublicKey ?? entry?.ml_kem_public_key ?? undefined,
          signingPublicKey: entry?.signingPublicKey ?? entry?.signing_public_key ?? null,
        };
      })
      .filter(Boolean);

    const apiPublicKey = user.publicKey || user.public_key;
    if (!normalizedKeys.length && apiPublicKey) {
      normalizedKeys.push({
        publicKey: String(apiPublicKey).replace(/\s+/g, ""),
        deviceId: apiDeviceId,
        mlKemPublicKey: undefined,
        signingPublicKey: null,
      });
    }

    const serializedKeys = serializeContactPublicKeys(normalizedKeys, apiDeviceId);
    const normalized = {
      ...user,
      userId: user.userId || user.user_id || userId,
      deviceId: apiDeviceId,
      publicKeys: normalizedKeys,
      publicKey: serializedKeys ?? apiPublicKey ?? null,
      isBlocked: false,
      isMuted: false,
    } as any;

    await db.insertUser(normalized);
    state.setUser?.(normalized);
    return resolveContactPublicKey(normalized);
  } catch (err) {
    console.warn("[community] failed to refresh contact keys", userId, err);
    return undefined;
  }
}

async function resolveRecipientPublicKey(state: any, receiverId: string): Promise<string | undefined> {
  const existing = resolveContactPublicKeyByUserId(state, receiverId);
  if (existing) return existing;
  return fetchAndStoreContactByUserId(state, receiverId);
}

function buildCommunityUpdatePayload(payload: CommunityUpdatePayloadInput): string {
  const payloadObj = {
    communityId: payload.communityId,
    communityKey: payload.communityKey,
    encryptedData: payload.encryptedData ?? "",
    iv: payload.iv ?? "",
    version: payload.version ?? 1,
    updatedAt: payload.updatedAt ?? nowMs(),
    reason: payload.reason || CommunityUpdateReason.COMMUNITY_KEY,
    memberId: payload.memberId ?? null,
    memberRole: payload.memberRole ?? null,
  };

  return JSON.stringify(payloadObj);
}

/**
 * Sends a COMMUNITY_UPDATED frame (encrypted to each recipient's pubkey)
 * to a specific list of community members. Exported so peer-assisted
 * recovery can re-share community keys with a restored peer.
 *
 * @platform-only: re-export of existing private function so the recovery
 * handler can call it; no new logic added.
 */
export async function sendCommunityUpdateToRecipients(params: {
  community: Community;
  payloadJson: string;
  senderId: string;
  recipients: Member[];
}): Promise<void> {
  const state = useStore.getState() as any;
  const mySecretKey = resolveMySecretKey(state);
  const myPublicKey = resolveMyPublicKey(state);

  if (!mySecretKey || !params.senderId) {
    console.warn("[community] missing sender credentials for COMMUNITY_UPDATED");
    return;
  }

  const timestamp = nowMs();

  for (const member of params.recipients) {
    if (!member?.userId) continue;
    const receiverId = member.userId;
    const receiverPublicKey =
      receiverId === params.senderId
        ? myPublicKey
        : await resolveRecipientPublicKey(state, receiverId);

    if (!receiverPublicKey) {
      console.warn("[community] missing public key for COMMUNITY_UPDATED recipient", receiverId);
      continue;
    }

    const encrypted = encryptDMMessageWithPublicKey(
      {
        id: generateUUID(),
        type: MessageType.COMMUNITY_UPDATED,
        contentType: "SYSTEM",
        content: params.payloadJson,
        receiverId,
      },
      receiverPublicKey
    );

    const messageId = encrypted?.id || generateUUID();
    if (!encrypted?.encryptedContent || !encrypted?.nonce) {
      console.warn("[community] failed to encrypt COMMUNITY_UPDATED for", receiverId);
      continue;
    }

    sendWebSocket(WebSocketEventType.MESSAGE_SEND, {
      id: messageId,
      type: MessageType.COMMUNITY_UPDATED,
      appId: appConfig.appId,
      senderId: params.senderId,
      receiverId,
      communityId: params.community.id,
      status: "SENT",
      createdAt: timestamp,
      updatedAt: timestamp,
      contentType: "SYSTEM",
      encryptedContent: encrypted.encryptedContent,
      nonce: encrypted.nonce,
    });
  }
}

async function syncCommunityGroups(params: {
  community: Community;
  communityKey: string;
}): Promise<void> {
  const state = useStore.getState() as any;
  const groups = normalizeCommunityGroups(params.community);
  if (groups.length === 0) return;

  for (const group of groups) {
    const groupId = group.id;
    if (!groupId) continue;
    const threadKey = communityGroupThreadKey(groupId);

    const existing = (state.threadMap?.[threadKey] || state.threadMap?.[groupId]) as Thread | undefined;
    const existingVersion = existing?.version ?? 0;
    const newVersion = existingVersion + 1;

    const groupPlainData = {
      name: group.name,
      description: existing?.description || null,
      communityId: params.community.id,
      ownerId: params.community.ownerId,
      isDefaultGroup: !!group.isDefault,
      isAnnouncementGroup: !!group.isBroadcast,
      isStandaloneGroup: false,
      members: params.community.members || [],
    };

    const nextGroupKeys: Record<number, string> = {
      ...(existing?.groupKeys || {}),
      [newVersion]: params.communityKey,
    };

    const updatedThread: Thread = {
      ...(existing || ({} as Thread)),
      id: threadKey,
      groupId,
      name: group.name,
      description: existing?.description || null,
      communityId: params.community.id,
      ownerId: params.community.ownerId,
      members: params.community.members || [],
      groupKeys: nextGroupKeys,
      currentKeyVersion: newVersion,
      isDefaultGroup: !!group.isDefault,
      isAnnouncementGroup: !!group.isBroadcast,
      isStandaloneGroup: false,
      createdAt: existing?.createdAt || params.community.createdAt || nowMs(),
      updatedAt: params.community.updatedAt || nowMs(),
      version: newVersion,
    } as Thread;

    state.setThread?.(updatedThread);
    await db.insertGrp(updatedThread as any);
    if (groupId !== threadKey && state.threadMap?.[groupId]) {
      state.removeThread?.(groupId);
      await db.deleteThread(groupId);
    }

    if (group.isDefault || group.isBroadcast) {
      const encrypted = encryptWithKey(groupPlainData, params.communityKey);
      await coreApi.createOrUpdateGroup({
        threadKey: groupId,
        version: newVersion,
        encryptedData: encrypted.encryptedData,
        iv: encrypted.iv,
        createdAt: Number(existing?.createdAt || params.community.createdAt || nowMs()),
        updatedAt: Number(params.community.updatedAt || nowMs()),
      });
    }
  }
}

async function syncDefaultCommunityGroups(params: {
  community: Community;
  communityKey: string;
}): Promise<void> {
  await syncCommunityGroups(params);
}

async function persistCommunityToLocal(community: Community): Promise<void> {
  const normalizedCommunity = normalizeCommunityMembers(community);
  const state = useStore.getState() as any;
  state.setCommunity?.(normalizedCommunity);
  if (typeof state.addUserCommunity === "function") {
    const existing = state.communityMap?.[normalizedCommunity.id];
    if (!existing) {
      state.addUserCommunity(normalizedCommunity);
    }
  }
  await db.insertCommunity(normalizedCommunity as any);
}

export async function createCommunity(input: {
  name: string;
  description?: string | null;
  imageBlob?: Blob | null;
  imageMimeType?: string | null;
}): Promise<Community> {
  const state = useStore.getState() as any;
  const apiToken = state.apiToken;
  const tokenData = apiToken ? parseJwt(apiToken) : {};
  const myUserId = tokenData.user_id;
  const myUsername = tokenData.username;

  if (!apiToken || !myUserId) {
    throw new Error("You must be logged in to create a community.");
  }

  const now = nowMs();
  const communityId = generateUUID();
  const communityKey = encodeBase64(coreCrypto.secretboxKeygen());
  const imageUrl = input.imageBlob
    ? await uploadCommunityImageBlob(input.imageBlob, input.imageMimeType)
    : null;

  const ownerMember: Member = {
    userId: myUserId,
    role: "owner",
    joinedAt: now,
  } as Member;

  const defaultGroupId = generateUUID();
  const broadcastGroupId = generateUUID();
  const defaultGroupThreadKey = communityGroupThreadKey(defaultGroupId);
  const broadcastGroupThreadKey = communityGroupThreadKey(broadcastGroupId);

  const community: Community = {
    id: communityId,
    name: input.name.trim(),
    description: input.description?.trim() || null,
    image: imageUrl,
    communityKey,
    createdAt: now,
    updatedAt: now,
    ownerId: myUserId,
    version: 1,
    members: [ownerMember],
    groups: [
      { id: defaultGroupId, name: "General", isDefault: true, isBroadcast: false },
      { id: broadcastGroupId, name: "Broadcast", isDefault: false, isBroadcast: true },
    ],
  } as Community;

  const communityPlainData = communityBlobPlainData(community);
  const encryptedCommunity = encryptWithKey(communityPlainData, communityKey);

  await coreApi.createOrUpdateCommunity({
    communityId: community.id,
    version: community.version || 1,
    encryptedData: encryptedCommunity.encryptedData,
    iv: encryptedCommunity.iv,
    createdAt: Number(community.createdAt),
    updatedAt: Number(community.updatedAt),
  });

  const groupMembers = [
    {
      userId: myUserId,
      role: "owner",
      joinedAt: now,
    },
  ];

  const defaultThread: Thread = {
    id: defaultGroupThreadKey,
    groupId: defaultGroupId,
    name: "General",
    description: null,
    type: "GROUP",
    communityId: communityId,
    ownerId: myUserId,
    members: groupMembers as any,
    groupKeys: { 1: communityKey },
    currentKeyVersion: 1,
    lastMessage: null,
    image: null,
    isDefaultGroup: true,
    isAnnouncementGroup: false,
    isStandaloneGroup: false,
    isMute: null,
    isBlock: null,
    unread: 0,
    membershipStatus: { status: "active", updatedAt: now, updatedBy: myUserId },
    hiloMeta: null,
    createdAt: now,
    updatedAt: now,
    version: 1,
  } as unknown as Thread;

  const broadcastThread: Thread = {
    id: broadcastGroupThreadKey,
    groupId: broadcastGroupId,
    name: "Broadcast",
    description: null,
    type: "GROUP",
    communityId: communityId,
    ownerId: myUserId,
    members: groupMembers as any,
    groupKeys: { 1: communityKey },
    currentKeyVersion: 1,
    lastMessage: null,
    image: null,
    isDefaultGroup: false,
    isAnnouncementGroup: true,
    isStandaloneGroup: false,
    isMute: null,
    isBlock: null,
    unread: 0,
    membershipStatus: { status: "active", updatedAt: now, updatedBy: myUserId },
    hiloMeta: null,
    createdAt: now,
    updatedAt: now,
    version: 1,
  } as unknown as Thread;

  const defaultGroupPlainData = {
    name: defaultThread.name,
    communityId: defaultThread.communityId,
    ownerId: defaultThread.ownerId,
    isDefaultGroup: true,
    isAnnouncementGroup: false,
    isStandaloneGroup: false,
    members: groupMembers,
  };
  const broadcastGroupPlainData = {
    name: broadcastThread.name,
    communityId: broadcastThread.communityId,
    ownerId: broadcastThread.ownerId,
    isDefaultGroup: false,
    isAnnouncementGroup: true,
    isStandaloneGroup: false,
    members: groupMembers,
  };

  const encryptedDefault = encryptWithKey(defaultGroupPlainData, communityKey);
  const encryptedBroadcast = encryptWithKey(broadcastGroupPlainData, communityKey);

  await coreApi.createOrUpdateGroup({
    threadKey: defaultGroupId,
    version: 1,
    encryptedData: encryptedDefault.encryptedData,
    iv: encryptedDefault.iv,
    createdAt: Number(defaultThread.createdAt),
    updatedAt: Number(defaultThread.updatedAt),
  });

  await coreApi.createOrUpdateGroup({
    threadKey: broadcastGroupId,
    version: 1,
    encryptedData: encryptedBroadcast.encryptedData,
    iv: encryptedBroadcast.iv,
    createdAt: Number(broadcastThread.createdAt),
    updatedAt: Number(broadcastThread.updatedAt),
  });

  state.setCommunity?.(community);
  state.addUserCommunity?.(community);
  state.setThread?.(defaultThread);
  state.setThread?.(broadcastThread);
  state.addUserGroup?.(defaultThread);
  state.addUserGroup?.(broadcastThread);

  await db.insertCommunity(community as any);
  await db.insertGrp(defaultThread as any);
  await db.insertGrp(broadcastThread as any);

  const payloadJson = buildCommunityUpdatePayload({
    communityId: community.id,
    communityKey,
    encryptedData: encryptedCommunity.encryptedData,
    iv: encryptedCommunity.iv,
    version: community.version || 1,
    updatedAt: community.updatedAt || now,
    reason: CommunityUpdateReason.COMMUNITY_KEY,
    memberId: null,
    memberRole: null,
  });

  await sendCommunityUpdateToRecipients({
    community,
    payloadJson,
    senderId: myUserId,
    recipients: community.members || [],
  });

  return community;
}

export async function createCommunityGroup(input: {
  community: Community;
  name: string;
  description?: string | null;
}): Promise<{ community: Community; group: CommunityGroup; thread: Thread }> {
  const state = useStore.getState() as any;
  const currentUserId = resolveCurrentUserId(state);
  if (!currentUserId) {
    throw new Error("Authentication required");
  }

  const trimmedName = (input.name || "").trim();
  if (!trimmedName) {
    throw new Error("Missing required field: name");
  }

  if (!canManageMembers(input.community, currentUserId)) {
    throw new Error("Only owner/admin can create groups");
  }

  const communityKey = input.community.communityKey;
  if (!communityKey) {
    throw new Error("Missing community key");
  }

  const now = nowMs();
  const groupId = generateUUID();
  const threadKey = communityGroupThreadKey(groupId);
  const description = input.description?.trim() || null;

  const newGroup: CommunityGroup = {
    id: groupId,
    name: trimmedName,
    isDefault: false,
    isBroadcast: false,
  };

  const updatedCommunity: Community = {
    ...input.community,
    groups: [...normalizeCommunityGroups(input.community), newGroup],
    updatedAt: now,
    version: (input.community.version || 1) + 1,
  } as Community;

  const encryptedCommunity = encryptWithKey(communityBlobPlainData(updatedCommunity), communityKey);

  await coreApi.createOrUpdateCommunity({
    communityId: updatedCommunity.id,
    version: updatedCommunity.version || 1,
    encryptedData: encryptedCommunity.encryptedData,
    iv: encryptedCommunity.iv,
    createdAt: Number(updatedCommunity.createdAt || now),
    updatedAt: Number(updatedCommunity.updatedAt || now),
  });

  const groupPlainData = {
    name: trimmedName,
    description: description ?? "",
    communityId: updatedCommunity.id,
    ownerId: updatedCommunity.ownerId,
    isDefaultGroup: false,
    isAnnouncementGroup: false,
    isStandaloneGroup: false,
    members: updatedCommunity.members || [],
  };

  const encryptedGroup = encryptWithKey(groupPlainData, communityKey);
  await coreApi.createOrUpdateGroup({
    threadKey: groupId,
    version: 1,
    encryptedData: encryptedGroup.encryptedData,
    iv: encryptedGroup.iv,
    createdAt: now,
    updatedAt: now,
  });

  const thread: Thread = {
    id: threadKey,
    groupId,
    name: trimmedName,
    description,
    communityId: updatedCommunity.id,
    ownerId: updatedCommunity.ownerId,
    members: updatedCommunity.members || [],
    groupKeys: { 1: communityKey },
    currentKeyVersion: 1,
    isDefaultGroup: false,
    isAnnouncementGroup: false,
    isStandaloneGroup: false,
    createdAt: now,
    updatedAt: now,
    version: 1,
  } as unknown as Thread;

  state.setThread?.(thread);
  await db.insertGrp(thread as any);
  await persistCommunityToLocal(updatedCommunity);

  const payloadJson = buildCommunityUpdatePayload({
    communityId: updatedCommunity.id,
    communityKey,
    encryptedData: encryptedCommunity.encryptedData,
    iv: encryptedCommunity.iv,
    version: updatedCommunity.version || 1,
    updatedAt: updatedCommunity.updatedAt || now,
    reason: CommunityUpdateReason.COMMUNITY_KEY,
    memberId: null,
    memberRole: null,
  });

  await sendCommunityUpdateToRecipients({
    community: updatedCommunity,
    payloadJson,
    senderId: currentUserId,
    recipients: updatedCommunity.members || [],
  });

  return { community: updatedCommunity, group: newGroup, thread };
}

export async function inviteCommunityMember(input: CommunityInviteInput): Promise<Community> {
  const state = useStore.getState() as any;
  const currentUserId = resolveCurrentUserId(state);

  if (!currentUserId) {
    throw new Error("Authentication required");
  }

  if (!canManageMembers(input.community, currentUserId)) {
    throw new Error("Insufficient permissions to invite members");
  }

  const role: CommunityRole = input.role || "member";
  const now = nowMs();
  const newMember: Member = {
    userId: input.userId,
    role,
    joinedAt: now,
  } as Member;

  if (input.community.members?.some((m) => m.userId === newMember.userId)) {
    throw new Error("User is already a member");
  }

  const updatedCommunity: Community = {
    ...input.community,
    members: [...(input.community.members || []), newMember],
    updatedAt: now,
    version: (input.community.version || 1) + 1,
    communityKey: (() => {
      const key = coreCrypto.secretboxKeygen();
      return encodeBase64(key);
    })(),
  } as Community;

  const encryptedCommunity = encryptWithKey(
    communityBlobPlainData(updatedCommunity),
    updatedCommunity.communityKey
  );

  try {
    await coreApi.createOrUpdateCommunity({
      communityId: updatedCommunity.id,
      version: updatedCommunity.version || 1,
      encryptedData: encryptedCommunity.encryptedData,
      iv: encryptedCommunity.iv,
      createdAt: Number(updatedCommunity.createdAt || now),
      updatedAt: Number(updatedCommunity.updatedAt || now),
    });
  } catch (error) {
    console.warn("[community] leave: createOrUpdateCommunity failed, continuing", error);
  }

  try {
    await syncDefaultCommunityGroups({
      community: updatedCommunity,
      communityKey: updatedCommunity.communityKey,
    });
  } catch (error) {
    console.warn("[community] leave: default group sync failed, continuing", error);
  }

  await persistCommunityToLocal(updatedCommunity);

  const payloadJson = buildCommunityUpdatePayload({
    communityId: updatedCommunity.id,
    communityKey: updatedCommunity.communityKey,
    encryptedData: encryptedCommunity.encryptedData,
    iv: encryptedCommunity.iv,
    version: updatedCommunity.version || 1,
    updatedAt: updatedCommunity.updatedAt || now,
    reason: CommunityUpdateReason.MEMBER_ADDED,
    memberId: newMember.userId,
    memberRole: role,
  });

  const recipients = updatedCommunity.members || [];
  await sendCommunityUpdateToRecipients({
    community: updatedCommunity,
    payloadJson,
    senderId: currentUserId,
    recipients,
  });

  return updatedCommunity;
}

export async function updateCommunityMemberRole(input: CommunityRoleUpdateInput): Promise<Community> {
  const state = useStore.getState() as any;
  const currentUserId = resolveCurrentUserId(state);

  if (!currentUserId) {
    throw new Error("Authentication required");
  }

  if (!canManageMembers(input.community, currentUserId)) {
    throw new Error("Insufficient permissions to update roles");
  }

  const isCurrentOwner = isOwner(input.community, currentUserId);
  const targetMember = input.community.members?.find((m) => m.userId === input.userId);
  const targetRole = normalizeMemberRole(targetMember?.role);

  if (!targetMember) {
    throw new Error("Member not found in community");
  }

  if (targetRole === "owner") {
    throw new Error("Cannot change owner role");
  }

  if (!isCurrentOwner && targetRole === "admin") {
    throw new Error("Admins cannot change other admins");
  }

  if (!isCurrentOwner && input.role === "admin") {
    throw new Error("Admins cannot promote members to admin");
  }

  const now = nowMs();
  const updatedMembers = (input.community.members || []).map((member) =>
    member.userId === input.userId
      ? ({ ...member, role: input.role } as Member)
      : member
  );

  const updatedCommunity: Community = {
    ...input.community,
    members: updatedMembers,
    updatedAt: now,
    version: (input.community.version || 1) + 1,
    communityKey: (() => {
      const key = coreCrypto.secretboxKeygen();
      return encodeBase64(key);
    })(),
  } as Community;

  const encryptedCommunity = encryptWithKey(
    communityBlobPlainData(updatedCommunity),
    updatedCommunity.communityKey
  );

  await coreApi.createOrUpdateCommunity({
    communityId: updatedCommunity.id,
    version: updatedCommunity.version || 1,
    encryptedData: encryptedCommunity.encryptedData,
    iv: encryptedCommunity.iv,
    createdAt: Number(updatedCommunity.createdAt || now),
    updatedAt: Number(updatedCommunity.updatedAt || now),
  });

  await syncDefaultCommunityGroups({
    community: updatedCommunity,
    communityKey: updatedCommunity.communityKey,
  });

  await persistCommunityToLocal(updatedCommunity);

  const payloadJson = buildCommunityUpdatePayload({
    communityId: updatedCommunity.id,
    communityKey: updatedCommunity.communityKey,
    encryptedData: encryptedCommunity.encryptedData,
    iv: encryptedCommunity.iv,
    version: updatedCommunity.version || 1,
    updatedAt: updatedCommunity.updatedAt || now,
    reason: CommunityUpdateReason.COMMUNITY_KEY,
    memberId: input.userId,
    memberRole: input.role,
  });

  await sendCommunityUpdateToRecipients({
    community: updatedCommunity,
    payloadJson,
    senderId: currentUserId,
    recipients: updatedCommunity.members || [],
  });

  return updatedCommunity;
}

export async function removeCommunityMember(input: CommunityInviteInput): Promise<Community> {
  const state = useStore.getState() as any;
  const currentUserId = resolveCurrentUserId(state);

  if (!currentUserId) {
    throw new Error("Authentication required");
  }

  if (!canManageMembers(input.community, currentUserId)) {
    throw new Error("Insufficient permissions to remove members");
  }

  const targetMember = input.community.members?.find((m) => m.userId === input.userId);
  const targetRole = normalizeMemberRole(targetMember?.role);
  if (!targetMember) {
    throw new Error("Member not found in community");
  }

  if (targetRole === "owner") {
    throw new Error("Cannot remove the community owner");
  }

  const isCurrentOwner = isOwner(input.community, currentUserId);
  if (!isCurrentOwner && targetRole === "admin") {
    throw new Error("Admins cannot remove other admins");
  }

  const now = nowMs();
  const updatedMembers = (input.community.members || []).filter((member) => member.userId !== input.userId);

  const updatedCommunity: Community = {
    ...input.community,
    members: updatedMembers,
    updatedAt: now,
    version: (input.community.version || 1) + 1,
    communityKey: (() => {
      const key = coreCrypto.secretboxKeygen();
      return encodeBase64(key);
    })(),
  } as Community;

  const encryptedCommunity = encryptWithKey(
    communityBlobPlainData(updatedCommunity),
    updatedCommunity.communityKey
  );

  await coreApi.createOrUpdateCommunity({
    communityId: updatedCommunity.id,
    version: updatedCommunity.version || 1,
    encryptedData: encryptedCommunity.encryptedData,
    iv: encryptedCommunity.iv,
    createdAt: Number(updatedCommunity.createdAt || now),
    updatedAt: Number(updatedCommunity.updatedAt || now),
  });

  await syncDefaultCommunityGroups({
    community: updatedCommunity,
    communityKey: updatedCommunity.communityKey,
  });

  await persistCommunityToLocal(updatedCommunity);

  const payloadJson = buildCommunityUpdatePayload({
    communityId: updatedCommunity.id,
    communityKey: updatedCommunity.communityKey,
    encryptedData: encryptedCommunity.encryptedData,
    iv: encryptedCommunity.iv,
    version: updatedCommunity.version || 1,
    updatedAt: updatedCommunity.updatedAt || now,
    reason: CommunityUpdateReason.MEMBER_REMOVED,
    memberId: input.userId,
    memberRole: targetRole,
  });

  await sendCommunityUpdateToRecipients({
    community: updatedCommunity,
    payloadJson,
    senderId: currentUserId,
    recipients: updatedCommunity.members || [],
  });

  return updatedCommunity;
}

export async function leaveCommunity(input: CommunityLeaveInput): Promise<void> {
  const state = useStore.getState() as any;
  const currentUserId = resolveCurrentUserId(state);

  if (!currentUserId) {
    throw new Error("Authentication required");
  }

  if (isOwner(input.community, currentUserId)) {
    throw new Error("Owner cannot leave the community");
  }

  const now = nowMs();
  const updatedMembers = (input.community.members || []).filter((member) => member.userId !== currentUserId);

  const updatedCommunity: Community = {
    ...input.community,
    members: updatedMembers,
    updatedAt: now,
    version: (input.community.version || 1) + 1,
    communityKey: (() => {
      const key = coreCrypto.secretboxKeygen();
      return encodeBase64(key);
    })(),
  } as Community;

  const encryptedCommunity = encryptWithKey(
    communityBlobPlainData(updatedCommunity),
    updatedCommunity.communityKey
  );

  await coreApi.createOrUpdateCommunity({
    communityId: updatedCommunity.id,
    version: updatedCommunity.version || 1,
    encryptedData: encryptedCommunity.encryptedData,
    iv: encryptedCommunity.iv,
    createdAt: Number(updatedCommunity.createdAt || now),
    updatedAt: Number(updatedCommunity.updatedAt || now),
  });

  await syncDefaultCommunityGroups({
    community: updatedCommunity,
    communityKey: updatedCommunity.communityKey,
  });

  const payloadJson = buildCommunityUpdatePayload({
    communityId: updatedCommunity.id,
    communityKey: updatedCommunity.communityKey,
    encryptedData: encryptedCommunity.encryptedData,
    iv: encryptedCommunity.iv,
    version: updatedCommunity.version || 1,
    updatedAt: updatedCommunity.updatedAt || now,
    reason: CommunityUpdateReason.EXIT_COMMUNITY,
    memberId: currentUserId,
  });

  await sendCommunityUpdateToRecipients({
    community: updatedCommunity,
    payloadJson,
    senderId: currentUserId,
    recipients: updatedCommunity.members || [],
  });

  await coreApi.leaveCommunity(input.community.id);

  state.removeCommunity?.(input.community.id);
  await db.deleteCommunity(input.community.id);
}

export async function handleCommunityUpdatedPayload(payloadJson: string, fallbackCommunityId?: string): Promise<void> {
  let parsed: any | null = null;
  try {
    parsed = JSON.parse(payloadJson);
  } catch (parseErr) {
    console.warn("[community] failed to parse COMMUNITY_UPDATED payload", parseErr);
    return;
  }

  const communityId = parsed?.communityId || parsed?.community_id || fallbackCommunityId;
  if (!communityId) {
    console.warn("[community] COMMUNITY_UPDATED missing communityId");
    return;
  }

  const communityKey = parsed?.communityKey || parsed?.community_key;
  if (!communityKey) {
    console.warn("[community] COMMUNITY_UPDATED missing communityKey");
    return;
  }

  let encryptedData = parsed?.encryptedData || parsed?.encrypted_data;
  let iv = parsed?.iv;
  const version = parsed?.version;
  const updatedAt = parsed?.updatedAt || parsed?.updated_at;

  if (!encryptedData || !iv) {
    try {
      const response = await coreApi.getCommunity(communityId);
      if (response?.community) {
        encryptedData = (response.community as any).encryptedData || (response.community as any).encrypted_data;
        iv = (response.community as any).iv;
      }
    } catch (err) {
      console.warn("[community] failed to fetch community blob", err);
    }
  }

  if (!encryptedData || !iv) {
    console.warn("[community] COMMUNITY_UPDATED missing encrypted data");
    return;
  }

  const decryptedCommunity = decryptWithKey(encryptedData, iv, communityKey) as Community | null;
  if (!decryptedCommunity) {
    console.warn("[community] failed to decrypt community blob");
    return;
  }

  const resolvedCommunity: Community = {
    ...decryptedCommunity,
    id: communityId,
    communityKey,
    version: (version ?? decryptedCommunity.version ?? 1) as any,
    updatedAt: (updatedAt ?? decryptedCommunity.updatedAt ?? nowMs()) as any,
  } as Community;

  await persistCommunityToLocal(resolvedCommunity);

  await syncCommunityGroups({
    community: resolvedCommunity,
    communityKey,
  });
}

export function getAnnouncementPermission(community: Community, userId?: string): boolean {
  const role = getCommunityMemberRole(community, userId);
  return role === "owner" || role === "admin";
}

/**
 * Re-shares a community's current key and metadata to a single peer by emitting
 * a COMMUNITY_UPDATED frame with reason=COMMUNITY_KEY. Used by peer-assisted
 * recovery when a contact has restored their account and asked us to re-share
 * communities we have in common.
 *
 * @platform-only: composes existing exports (encryptWithKey, buildCommunityUpdatePayload,
 * sendCommunityUpdateToRecipients) into one call for the recovery handler; no new logic.
 */
export async function reshareCommunityToPeer(
  community: Community,
  peerUserId: string,
  senderId: string,
): Promise<void> {
  const peer = community.members?.find((m) => m.userId === peerUserId);
  if (!peer) return;
  const communityKey = community.communityKey;
  if (!communityKey) return;

  const metadata = {
    id: community.id,
    name: community.name,
    description: community.description,
    image: community.image,
    members: community.members,
    ownerId: community.ownerId,
    createdAt: community.createdAt,
    updatedAt: community.updatedAt,
    version: community.version,
  };
  const encrypted = encryptWithKey(metadata, communityKey);

  const payloadJson = buildCommunityUpdatePayload({
    communityId: community.id,
    communityKey,
    encryptedData: encrypted.encryptedData,
    iv: encrypted.iv,
    version: community.version || 1,
    updatedAt: community.updatedAt || nowMs(),
    reason: CommunityUpdateReason.COMMUNITY_KEY,
    memberId: null,
    memberRole: null,
  });

  await sendCommunityUpdateToRecipients({
    community,
    payloadJson,
    senderId,
    recipients: [peer],
  });
}
